import { useEffect, useState } from 'react'
import { PageTitleService } from '../../hooks/usePageTitle'
import userService, {
  type UserMe,
  type AuthorDisplayPreference,
} from '../../services/userService'
import { LoadingSpinner } from '../../components/shared'
import { useAuth } from '../../context/auth'

export default function Profile() {
  const { features } = useAuth()
  const chatSharingEnabled = features?.chat_sharing_enabled ?? false

  const [user, setUser] = useState<UserMe | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [pwdSaving, setPwdSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    department: '',
  })

  const [sharingPrefs, setSharingPrefs] = useState({
    author_display_preference: 'full_name' as AuthorDisplayPreference,
    notify_on_share: true,
    notify_on_participant_join: false,
  })
  const [prefsSaving, setPrefsSaving] = useState(false)

  const [pwdForm, setPwdForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  useEffect(() => {
    PageTitleService.setTitle('Perfil', ['Usuário'])
    const load = async () => {
      try {
        const data = await userService.getMe()
        setUser(data)
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          department: data.profile?.department || '',
        })
        setSharingPrefs({
          author_display_preference:
            data.profile?.author_display_preference || 'full_name',
          notify_on_share: data.profile?.notify_on_share ?? true,
          notify_on_participant_join:
            data.profile?.notify_on_participant_join ?? false,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar perfil')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload: Partial<UserMe> = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        profile: { department: form.department },
      }
      const updated = await userService.updateMe(payload)
      setUser(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar perfil')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveSharingPrefs = async () => {
    setPrefsSaving(true)
    setError(null)
    try {
      const payload: Partial<UserMe> = {
        profile: {
          author_display_preference: sharingPrefs.author_display_preference,
          notify_on_share: sharingPrefs.notify_on_share,
          notify_on_participant_join: sharingPrefs.notify_on_participant_join,
        },
      }
      const updated = await userService.updateMe(payload)
      setUser(updated)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao salvar preferências'
      )
    } finally {
      setPrefsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setError('Confirmação de senha não confere')
      return
    }
    setPwdSaving(true)
    setError(null)
    try {
      await userService.changePassword(
        pwdForm.current_password,
        pwdForm.new_password
      )
      setPwdForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao trocar senha')
    } finally {
      setPwdSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-content-primary">
            Perfil do Usuário
          </h1>
          <p className="text-sm text-content-secondary">
            Atualize suas informações e gerencie sua senha
          </p>
        </div>
        <div className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs text-content-secondary">
          {user?.username}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-950/40 px-3 py-2 text-sm text-error-700 dark:text-error-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Dados Pessoais
            </h2>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded-md hover:bg-brand-600 transition disabled:opacity-60"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_first_name"
              >
                Nome
              </label>
              <input
                id="profile_first_name"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_last_name"
              >
                Sobrenome
              </label>
              <input
                id="profile_last_name"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_email"
              >
                E-mail
              </label>
              <input
                id="profile_email"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_department"
              >
                Departamento
              </label>
              <input
                id="profile_department"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={form.department}
                onChange={e => setForm({ ...form, department: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Alterar Senha
            </h2>
            <button
              onClick={handleChangePassword}
              disabled={pwdSaving}
              className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition disabled:opacity-60"
            >
              {pwdSaving ? 'Alterando...' : 'Alterar'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_password_current"
              >
                Senha atual
              </label>
              <input
                type="password"
                id="profile_password_current"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={pwdForm.current_password}
                onChange={e =>
                  setPwdForm({ ...pwdForm, current_password: e.target.value })
                }
              />
            </div>
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_password_new"
              >
                Nova senha
              </label>
              <input
                type="password"
                id="profile_password_new"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={pwdForm.new_password}
                onChange={e =>
                  setPwdForm({ ...pwdForm, new_password: e.target.value })
                }
              />
            </div>
            <div>
              <label
                className="text-xs text-gray-500 dark:text-gray-400"
                htmlFor="profile_password_confirm"
              >
                Confirmar nova senha
              </label>
              <input
                type="password"
                id="profile_password_confirm"
                className="w-full mt-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                value={pwdForm.confirm_password}
                onChange={e =>
                  setPwdForm({ ...pwdForm, confirm_password: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* Sharing Preferences - Only show when feature is enabled */}
        {chatSharingEnabled && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Preferências de Compartilhamento
              </h2>
              <button
                onClick={handleSaveSharingPrefs}
                disabled={prefsSaving}
                className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded-md hover:bg-brand-600 transition disabled:opacity-60"
              >
                {prefsSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label
                  className="text-xs text-gray-500 dark:text-gray-400"
                  htmlFor="profile_author_display"
                >
                  Exibição do Nome
                </label>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                  Como seu nome aparece para outros em chats compartilhados
                </p>
                <select
                  id="profile_author_display"
                  className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={sharingPrefs.author_display_preference}
                  onChange={e =>
                    setSharingPrefs({
                      ...sharingPrefs,
                      author_display_preference: e.target
                        .value as AuthorDisplayPreference,
                    })
                  }
                >
                  <option value="full_name">Nome Completo</option>
                  <option value="username">Nome de Usuário</option>
                  <option value="anonymous">Anônimo</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500"
                    checked={sharingPrefs.notify_on_share}
                    onChange={e =>
                      setSharingPrefs({
                        ...sharingPrefs,
                        notify_on_share: e.target.checked,
                      })
                    }
                  />
                  <div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Notificar ao Compartilhar
                    </span>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Receber notificação quando alguém compartilhar um chat
                      comigo
                    </p>
                  </div>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500"
                    checked={sharingPrefs.notify_on_participant_join}
                    onChange={e =>
                      setSharingPrefs({
                        ...sharingPrefs,
                        notify_on_participant_join: e.target.checked,
                      })
                    }
                  />
                  <div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Notificar Novos Participantes
                    </span>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Receber notificação quando alguém entrar em meus chats
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
